import { Row, Col, Card, Table, Button, Tag, DatePicker, Tooltip, Input, Select } from "antd";
import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import useRequest from "../../hooks/useRequest";
import { ShowToast, Severty } from "../../helper/toast";
import ShowTotal from "../../components/ShowTotal";
import moment from "moment";
import apiPath from "../../constants/apiPath";
import lang from "../../helper/lang";
import useDebounce from "../../hooks/useDebounce";
import { AuthContext } from "../../context/AuthContext";
import { USERTYPE } from "../../constants/conts";
import { ExclamationCircleOutlined, UndoOutlined } from "@ant-design/icons";
import FilterBar, { FilterBarFilters } from "../../components/FilterBar";
import confirm from "antd/lib/modal/confirm";

const { RangePicker } = DatePicker;
const Search = Input.Search;

function Tournaments() {
    const sectionName = "Tournament";
    const navigate = useNavigate();
    const { userProfile } = useContext(AuthContext);
    const { request } = useRequest();
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [refresh, setRefresh] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [startDate, setStartDate] = useState();
    const [endDate, setEndDate] = useState();
    const debouncedSearchText = useDebounce(searchText, 300);
    const [filters, setFilters] = useState({ status: undefined });
    const [showFilters, setShowFilters] = useState(false);
    const { Option } = Select;
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

    const fetchData = (paginationParam, filtersParam) => {
        setLoading(true);
        const params = new URLSearchParams();
        if (startDate) {
            params.append("startDate", startDate);
        }
        if (endDate) {
            params.append("endDate", endDate);
        }
        if (debouncedSearchText) {
            params.append("search", debouncedSearchText);
        }

        // Status filter
        if (filtersParam.status !== undefined && filtersParam.status !== null) {
            params.append("status", filtersParam.status);
        }

        // Pagination parameters
        params.append("page", paginationParam.current);
        params.append("limit", paginationParam.pageSize);

        request({
            url: `${apiPath.tournamentsList}?${params.toString()}`,
            method: "GET",
            onSuccess: ({ data }) => {
                setLoading(false);
                const listData = data?.data?.docs || data?.data || data?.docs || (Array.isArray(data) ? data : []);
                setList(listData);
                setPagination({
                    current: Number(data?.page) || paginationParam.current,
                    total: Number(data?.total) || Number(data?.totalDocs) || listData.length || 0,
                    pageSize: Number(data?.limit) || paginationParam.pageSize,
                });
            },
            onError: (error) => {
                console.error(error);
                setLoading(false);
                ShowToast(error, Severty.ERROR);
            },
        });
    };

    const handleChange = (paginationParam, filtersParam) => {
        fetchData(paginationParam, filters);
    };

    const onSearch = (e) => {
        setSearchText(e.target.value);
        setPagination({ current: 1, pageSize: pagination.pageSize });
    };

    const resetData = () => {
        setSearchText("");
        setStartDate("");
        setEndDate("");
        setFilters({
            status: undefined,
        });
        setRefresh((prev) => !prev);
    };

    const handleChangeDate = (e) => {
        if (e != null) {
            setStartDate(moment(e[0]._d).format("YYYY-MM-DD"));
            setEndDate(moment(e[1]._d).format("YYYY-MM-DD"));
        } else {
            setStartDate();
            setEndDate();
        }
    };

    const confirmStatus = (id, currentStatus) => {
        setTimeout(() => {
            confirm({
                maskClosable: true,
                icon: <ExclamationCircleOutlined />,
                className: "tab_modal",
                cancelButtonProps: { color: "red" },
                content: (
                    <p
                        style={{
                            fontSize: "24px",
                            fontWeight: "bold",
                            textAlign: "center",
                        }}
                    >
                        Are you sure you want to change the status?
                    </p>
                ),
                onOk() {
                    request({
                        url: `${apiPath.changeTournamentStatus}/${id}`,
                        method: "PUT",
                        data: {},
                        onSuccess: (data) => {
                            fetchData(pagination, filters);
                            ShowToast(data?.message || "Status Changed Successfully", Severty.SUCCESS);
                        },
                        onError: (error) => {
                            setLoading(false);
                            ShowToast(error, Severty.ERROR);
                        },
                    });
                },
                onCancel() {
                    console.log("Cancel");
                },
            });
        }, 5);
    };

    const handleDelete = (id) => {
        setTimeout(() => {
            confirm({
                maskClosable: true,
                icon: <ExclamationCircleOutlined />,
                className: "tab_modal",
                cancelButtonProps: { color: "red" },
                content: (
                    <p
                        style={{
                            fontSize: "24px",
                            fontWeight: "bold",
                            textAlign: "center",
                        }}
                    >
                        Are you sure you want to delete this Tournament permanently ?
                    </p>
                ),
                onOk() {
                    request({
                        url: `${apiPath.deleteTournament}/${id}`,
                        method: "DELETE",
                        data: {},
                        onSuccess: (data) => {
                            fetchData(pagination, filters);
                            ShowToast(
                                `${data?.message || "Tournament deleted successfully"}`,
                                Severty.SUCCESS
                            );
                        },
                        onError: (error) => {
                            console.error("error-->", error);
                            const message = error?.response?.data?.message || error || "Failed to delete tournament";
                            setLoading(false);
                            ShowToast(message, Severty.ERROR);
                        },
                    });
                },
                onCancel() {
                    console.log("Cancel");
                },
            });
        }, 5);
    };

    const handleFilterChange = (filterName, value) => {
        setFilters((prev) => ({ ...prev, [filterName]: value }));
        setPagination((prev) => ({ ...prev, current: 1 }));
    };

    const getStatusTag = (status, isActive) => {
        const s = (status || (isActive ? "ACTIVE" : "INACTIVE")).toUpperCase();
        let color = "default";
        switch (s) {
            case "ACTIVE":
                color = "green";
                break;
            case "LIVE":
                color = "blue";
                break;
            case "UPCOMING":
                color = "orange";
                break;
            case "COMPLETED":
                color = "purple";
                break;
            case "CANCELLED":
                color = "red";
                break;
            case "DRAFT":
                color = "gold";
                break;
            default:
                color = "default";
        }
        return <Tag color={color}>{s}</Tag>;
    };

    const columns = [
        {
            title: `Name`,
            key: "name",
            dataIndex: "name",
            width: "15%",
            render: (_, record) => {
                const nameVal = record.name || record.title || "-";
                const displayName = nameVal.length > 35 ? `${nameVal.substring(0, 35)}...` : nameVal;
                return (
                    <a
                        onClick={() => navigate(`/admin/tournaments/view/${record._id || record.id}`)}
                        style={{ fontWeight: "600", color: "#1890ff" }}
                    >
                        {displayName}
                    </a>
                );
            },
            sorter: (a, b) => {
                let nameA = (a.name || a.title || "").toLowerCase();
                let nameB = (b.name || b.title || "").toLowerCase();
                if (nameA < nameB) return -1;
                if (nameA > nameB) return 1;
                return 0;
            },
        },
        {
            title: `Description`,
            key: "description",
            dataIndex: "description",
            width: "20%",
            render: (_, { description }) => {
                if (!description) return "-";
                return description.length > 35 ? `${description.substring(0, 35)}...` : description;
            },
        },
        {
            title: `Created By`,
            key: "createdBy",
            dataIndex: "createdBy",
            width: "15%",
            render: (createdBy) => {
                return createdBy?.name || createdBy?.username || "-";
            },
        },
        {
            title: `Privacy`,
            key: "privacy",
            dataIndex: "privacy",
            width: "10%",
            render: (privacy) => {
                return privacy ? <Tag color={privacy === "PUBLIC" ? "blue" : "magenta"}>{privacy}</Tag> : "-";
            },
        },
        {
            title: `Start Date`,
            key: "startDate",
            dataIndex: "startDate",
            width: "10%",
            render: (startDate) => startDate ? moment(startDate).format("DD-MM-YYYY") : "-",
            sorter: (a, b) => new Date(a.startDate || 0) - new Date(b.startDate || 0),
        },
        {
            title: `End Date`,
            key: "endDate",
            dataIndex: "endDate",
            width: "10%",
            render: (endDate) => endDate ? moment(endDate).format("DD-MM-YYYY") : "-",
            sorter: (a, b) => new Date(a.endDate || 0) - new Date(b.endDate || 0),
        },
        {
            title: `${lang("Added On")}`,
            key: "createdAt",
            dataIndex: "createdAt",
            width: "10%",
            render: (createdAt) => createdAt ? moment(createdAt).format("DD-MM-YYYY") : "-",
            sorter: (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0),
        },
        {
            title: `${lang("Action")}`,
            width: "8%",
            render: (_, record) => {
                return (
                    <div style={{ display: "flex", gap: "8px" }}>
                        <Tooltip
                            title="View Tournament"
                            color={"purple"}
                            key="viewTournament"
                        >
                            <Button
                                onClick={() => {
                                    navigate(`/admin/tournaments/view/${record._id || record.id}`);
                                }}
                            >
                                <i className="fa fa-eye"></i>
                            </Button>
                        </Tooltip>
                        {/* <Tooltip
                            title="Delete Tournament"
                            color={"purple"}
                            key="deleteTournament"
                        >
                            <Button
                                onClick={() => {
                                    handleDelete(record._id || record.id);
                                }}
                            >
                                <i className="fa fa-solid fa-trash"></i>
                            </Button>
                        </Tooltip> */}
                    </div>
                );
            },
        },
    ];

    useEffect(() => {
        fetchData(pagination, filters);
    }, [filters, refresh, startDate, endDate, debouncedSearchText]);

    return (
        <>
            <div className="tabled">
                <Row gutter={[24, 0]}>
                    <Col xs={24} xl={24}>
                        <Card
                            bordered={false}
                            className="criclebox tablespace mb-24"
                            title={sectionName + ` ${lang("Manager")}`}
                            extra={
                                <FilterBar
                                    showFilters={showFilters}
                                    onToggle={() => setShowFilters((v) => !v)}
                                    searchContent={
                                        <Search
                                            size="large"
                                            onChange={onSearch}
                                            value={searchText}
                                            onPressEnter={onSearch}
                                            allowClear
                                            placeholder={`Search By Tournament Name`}
                                        />
                                    }
                                />
                            }
                        >
                            {showFilters && (
                                <FilterBarFilters>
                                    <Select
                                        placeholder="Status"
                                        value={filters.status}
                                        onChange={(value) => handleFilterChange("status", value)}
                                        style={{ width: 150 }}
                                        allowClear
                                    >
                                        <Option value="true">Active</Option>
                                        <Option value="false">Inactive</Option>
                                    </Select>
                                    <RangePicker
                                        disabledDate={(current) =>
                                            current && current.isAfter(moment().endOf("day"))
                                        }
                                        onChange={handleChangeDate}
                                        value={
                                            startDate && endDate
                                                ? [moment(startDate), moment(endDate)]
                                                : null
                                        }
                                    />
                                    <Button
                                        onClick={() => resetData()}
                                        type="primary"
                                        icon={<UndoOutlined />}
                                    >
                                        Reset
                                    </Button>
                                </FilterBarFilters>
                            )}
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "end",
                                    alignItems: "center",
                                    marginBottom: "10px",
                                    marginRight: "24px",
                                    flexWrap: "wrap",
                                }}
                            >
                                <h4 className="text-right m-0">
                                    {pagination.total ? ShowTotal(pagination.total) : ShowTotal(0)}
                                </h4>
                            </div>

                            <div className="table-responsive customPagination customPaginationmain-newwwww">
                                <Table
                                    loading={loading}
                                    columns={columns}
                                    dataSource={list}
                                    rowKey={(record) => record._id || record.id}
                                    pagination={{
                                        current: pagination.current,
                                        defaultPageSize: 10,
                                        responsive: true,
                                        total: pagination.total,
                                        showSizeChanger: true,
                                        showQuickJumper: true,
                                        pageSizeOptions: ["10", "20", "30", "50"],
                                    }}
                                    onChange={handleChange}
                                    className="ant-border-space"
                                />
                            </div>
                        </Card>
                    </Col>
                </Row>
            </div>
        </>
    );
}

export default Tournaments;
